import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { Botao, Campo, Cartao, Escolha } from "@/components/casca/formulario";
import type { Tenant } from "@/lib/tenants/types";
import { salvarIntegracoesAction } from "@/web/src/acoes-config";

type Etapa = { etapa: string; ok: boolean; detalhe: string };

/**
 * Agenda e ficha: no banco (padrão) ou no Google da clínica. O teste percorre
 * o MESMO caminho do agente — grava, lê de volta e apaga.
 */
export function FormGoogle({ integracoes, slug }: { integracoes: Tenant["integracoes"]; slug: string }) {
  const [i, setI] = useState({
    agenda: integracoes.agenda,
    ficha: integracoes.ficha,
    calendarId: integracoes.calendarId ?? "",
    spreadsheetId: integracoes.spreadsheetId ?? "",
    aba: integracoes.aba ?? "Clientes",
  });
  const [pendente, iniciar] = useTransition();
  const [testando, setTestando] = useState<"agenda" | "ficha" | null>(null);
  const [resultado, setResultado] = useState<{ de: string; etapas: Etapa[] } | null>(null);

  async function testar(qual: "agenda" | "ficha") {
    setTestando(qual);
    setResultado(null);
    try {
      const r = await fetch(`/api/sandbox/google/${qual === "agenda" ? "teste" : "teste-ficha"}?tenant=${slug}`, { method: "POST" });
      const j = (await r.json()) as { etapas?: Etapa[]; erro?: string };
      if (j.erro) toast.error(j.erro);
      setResultado({ de: qual === "agenda" ? "Agenda" : "Planilha", etapas: j.etapas ?? [] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setTestando(null);
    }
  }

  return (
    <Cartao>
      <p className="rotulo mb-1.5">Google</p>
      <h2 className="text-base font-bold text-[var(--color-tinta)]">Agenda e Planilha</h2>
      <p className="mt-1 mb-4 text-[13.5px] leading-relaxed text-[var(--color-suave)]">
        No banco, funciona sem configurar nada. No Google, a clínica vê os horários na agenda dela —
        compartilhe a agenda e a planilha com a conta de serviço do LummiChat.
      </p>

      <Escolha rotulo="Agenda" value={i.agenda} onChange={(e) => setI({ ...i, agenda: e.target.value as "memoria" | "google" })}>
        <option value="memoria">No LummiChat</option>
        <option value="google">Google Agenda</option>
      </Escolha>
      <Campo rotulo="ID da agenda" value={i.calendarId} disabled={i.agenda !== "google"} onChange={(e) => setI({ ...i, calendarId: e.target.value })} placeholder="xxxx@group.calendar.google.com" ajuda="Em Configurações da agenda → “ID da agenda”." />

      <Escolha rotulo="Ficha" value={i.ficha} onChange={(e) => setI({ ...i, ficha: e.target.value as "memoria" | "google" })}>
        <option value="memoria">No LummiChat</option>
        <option value="google">Planilha do Google</option>
      </Escolha>
      <Campo rotulo="ID da planilha" value={i.spreadsheetId} disabled={i.ficha !== "google"} onChange={(e) => setI({ ...i, spreadsheetId: e.target.value })} placeholder="o trecho da URL entre /d/ e /edit" />
      <Campo rotulo="Aba" value={i.aba} disabled={i.ficha !== "google"} onChange={(e) => setI({ ...i, aba: e.target.value })} placeholder="Clientes" />

      {resultado && (
        <div className="mt-3 rounded-xl bg-[var(--color-painel)] p-3 text-[13px]">
          <p className="mb-1 font-medium text-[var(--color-tinta)]">
            {resultado.de}: {resultado.etapas.every((e) => e.ok) ? "tudo certo" : "algo falhou"}
          </p>
          <ul className="space-y-0.5 text-[11px] text-[var(--color-suave)]">
            {resultado.etapas.map((e, k) => (
              <li key={k} className="flex items-start gap-1.5">
                <Icone nome={e.ok ? "check" : "alerta"} className={`mt-px h-3 w-3 shrink-0 ${e.ok ? "text-[var(--color-marca)]" : "text-[var(--color-alerta)]"}`} />
                <span>
                  {e.etapa}: {e.detalhe}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Botao
          icone="salvar"
          disabled={pendente}
          onClick={() =>
            iniciar(async () => {
              const r = await salvarIntegracoesAction(i);
              if (!r.ok) toast.error(r.erro);
              else toast.success("Integrações salvas.");
            })
          }
        >
          {pendente ? "Salvando…" : "Salvar"}
        </Botao>
        <Botao variante="secundario" icone="bancada" disabled={testando !== null || integracoes.agenda !== "google"} onClick={() => void testar("agenda")}>
          {testando === "agenda" ? "Testando…" : "Testar agenda"}
        </Botao>
        <Botao variante="secundario" icone="bancada" disabled={testando !== null || integracoes.ficha !== "google"} onClick={() => void testar("ficha")}>
          {testando === "ficha" ? "Testando…" : "Testar planilha"}
        </Botao>
      </div>
      {(integracoes.agenda === "google" || integracoes.ficha === "google") && (
        <p className="mt-2 text-xs leading-relaxed text-[var(--color-fraco)]">O teste grava um item de teste no Google e apaga em seguida — só depois de salvar.</p>
      )}
    </Cartao>
  );
}
